/**
 * @file
 * Unified taxonomy pseudo filter handler.
 *
 * Provides comprehensive functionality for the new taxonomy pseudo filter,
 * combining features from both hierarchical checkboxes and parent selector plugins.
 *
 * @param {object} $ - jQuery.
 * @param {object} Drupal - The Drupal object.
 * @param {Function} once - The once function.
 * @param {Array} dataLayer - The GTM dataLayer array.
 */

(function ($, Drupal, once, dataLayer) {
  'use strict';

  /**
   * TaxonomyPseudoFilterHandler class for unified taxonomy filtering.
   * Extends PseudoFilterHandler to add comprehensive taxonomy functionality.
   */
  class TaxonomyPseudoFilterHandler extends window.PseudoFilterHandler {

    /**
     * Constructor.
     *
     * @param {HTMLElement} wrapper - The wrapper element.
     * @param {Object} settings - The settings object.
     */
    constructor(wrapper, settings) {
      super(wrapper, settings);

      // Parse configuration from data attributes
      this.enableTreeControls = wrapper.dataset.treeControls === 'true';
      this.maxDepth = parseInt(wrapper.dataset.maxDepth) || 0;
      this.vocabulary = wrapper.dataset.vocabulary;
      this.isMultiple = wrapper.dataset.multiple !== 'false';

      // Parse mirrored filters configuration
      this.mirroredFilters = wrapper.dataset.mirroredFilters ? wrapper.dataset.mirroredFilters.split(',') : [];

      // Store tree-specific properties
      this.toggleButtons = null;
      this.treeItems = null;
      this.boundHandleKeyboardNavigation = this.handleKeyboardNavigation.bind(this);
      this.boundHandleToggleClick = this.handleToggleClick.bind(this);
      this.boundHandleRadioKeydown = this.handleRadioKeydown.bind(this);

      // boolean indicating when filters are in the process of being cleared
      this.areFiltersClearing = false;
    }

    /**
     * Initialize the handler.
     */
    initialize() {
      super.initialize();

      if (this.enableTreeControls) {
        this.initializeTreeControls();
        this.initializeTreeState();
      }

      if (!this.isMultiple) {
        this.initializeRadioNavigation();
      }

      this.wrapper.parentElement.addEventListener('clear', (event) => {
        this.areFiltersClearing = event.detail === 'start';
      });
    }

    /**
     * Attach the keyboard navigation listener for the single-select radio group.
     *
     * Removes any existing listener first so this method is safe to call
     * after re-attach cycles (e.g. AJAX reconnects).
     */
    initializeRadioNavigation() {
      this.wrapper.removeEventListener('keydown', this.boundHandleRadioKeydown);
      this.wrapper.addEventListener('keydown', this.boundHandleRadioKeydown);
    }

    /**
     * Handle keydown events within the single-select radio group.
     *
     * Arrow keys move focus without selecting. Enter confirms the
     * focused option.
     *
     * @param {KeyboardEvent} event - The keydown event.
     */
    handleRadioKeydown(event) {
      const radios = Array.from(this.allCheckboxes);
      if (!radios.length) {
        return;
      }

      switch (event.key) {
        case 'ArrowDown':
        case 'ArrowRight': {
          event.preventDefault();
          const currentIndex = radios.indexOf(document.activeElement);
          const nextIndex = (currentIndex + 1) % radios.length;
          radios[nextIndex].focus();
          this.scrollOptionIntoView(radios[nextIndex]);
          break;
        }

        case 'ArrowUp':
        case 'ArrowLeft': {
          event.preventDefault();
          const currentIndex = radios.indexOf(document.activeElement);
          const prevIndex = (currentIndex - 1 + radios.length) % radios.length;
          radios[prevIndex].focus();
          this.scrollOptionIntoView(radios[prevIndex]);
          break;
        }

        case 'Enter': {
          const focused = document.activeElement;
          if (focused && focused.type === 'radio' && radios.includes(focused)) {
            event.preventDefault();
            focused.checked = true;
            const changeEvent = new Event('change', {bubbles: true});
            focused.dispatchEvent(changeEvent);
          }
          break;
        }
      }
    }

    /**
     * Scroll the option into view within the scrollable dropdown container.
     *
     * @param {HTMLElement} radio - The radio input element.
     */
    scrollOptionIntoView(radio) {
      const option = radio.closest('.pseudo-filter-option');
      if (option) {
        option.scrollIntoView({block: 'nearest'});
      }
    }

    /**
     * Set up checkbox management and mapping.
     * Override to use the correct selector for taxonomy checkboxes.
     */
    setupCheckboxes() {
      if (this.isMultiple) {
        this.allCheckboxes = this.wrapper.querySelectorAll('.term-checkbox');
      }
      else {
        // Single-selection mode renders radio inputs instead of checkboxes.
        this.allCheckboxes = this.wrapper.querySelectorAll('.term-radio');
      }
      this.buildTermMappings();
      this.syncWithRealFilter();
      this.updateSummaryDisplay();

      if (this.mirroredFilters.length) {
        this.initializeMirroredFilters();
      }
    }

    /**
     * Get selected term IDs, excluding the empty "All" sentinel value used in
     * single-select mode. In multi-select mode, includes children of selected
     * parent terms when tree controls are disabled.
     *
     * @return {Array} Array of selected term IDs.
     */
    getSelectedTermsWithChildren() {
      if (!this.isMultiple) {
        const selected = [];
        this.allCheckboxes.forEach(radio => {
          if (radio.checked && radio.value !== '') {
            selected.push(radio.value);
          }
        });
        return selected;
      }

      const selectedTerms = new Set();

      this.allCheckboxes.forEach(checkbox => {
        if (checkbox.checked && !checkbox.indeterminate) {
          const termId = checkbox.value;
          selectedTerms.add(termId);

          if (!this.enableTreeControls) {
            const term = this.termMap.get(termId);
            if (term && term.children && term.children.length > 0) {
              this.addChildrenToSet(term.children, selectedTerms);
            }
          }
        }
      });

      return Array.from(selectedTerms);
    }

    /**
     * Update the summary text. In single-select mode show the currently
     * selected option's label (falling back to the "All" radio's label).
     */
    updateSummaryDisplay() {
      if (this.isMultiple) {
        super.updateSummaryDisplay();
        return;
      }
      const summaryElement = this.wrapper.querySelector('details.pseudo-filter-details summary .summary-placeholder');
      if (!summaryElement) {
        return;
      }
      const checked = this.wrapper.querySelector('.term-radio:checked')
        || this.wrapper.querySelector('.term-radio--all');
      if (!checked) {
        return;
      }
      const label = this.wrapper.querySelector('label[for="' + checked.id + '"]');
      if (label) {
        summaryElement.textContent = label.textContent.trim();
      }
      const summary = summaryElement.closest('summary');
      const filterLabelEl = this.wrapper.querySelector('.pseudo-filter-label');
      if (summary && filterLabelEl) {
        summary.setAttribute('aria-label', filterLabelEl.textContent.trim() + ': ' + summaryElement.textContent);
      }
    }

    /**
     * Reflect the radio selection back onto the "selected" option modifier so
     * styling stays in sync, then defer to the tree-aware change logic.
     *
     * @param {Event} event - The change event.
     */
    handleCheckboxChange(event) {
      if (!this.isMultiple) {
        const radio = event.target;
        this.wrapper.querySelectorAll('.pseudo-filter-option--selected').forEach(option => {
          option.classList.remove('pseudo-filter-option--selected');
        });
        const option = radio.closest('.pseudo-filter-option');
        if (option) {
          option.classList.add('pseudo-filter-option--selected');
        }
      }

      const checkbox = event.target;
      const termId = checkbox.value;
      const isChecked = checkbox.checked;

      if (this.enableTreeControls) {
        this.handleHierarchicalSelection(termId, isChecked);
      }
      else {
        this.handleParentChildSelection(termId, isChecked);
      }

      this.syncRealFilterWithCheckboxes();

      this.updateSummaryDisplay();

      if (this.isAjaxView && this.exposedForm && window.tnViewsPseudoFilterAutoSubmit) {
        // The "All" sentinel (empty value) has no real filter option to track.
        if (termId !== '') {
          this.trackFilterSelection(termId, isChecked);
        }
        // Skip the per-input submit while a clear is running; the clear handler
        // performs a single authoritative submit and these would race with it.
        if (!window.tnViewsPseudoFiltersClearing) {
          this.debouncedSubmit();
        }
      }

      // Auto-close the dropdown after a single-select choice.
      if (!this.isMultiple) {
        this.closeDropdown();
      }
    }

    /**
     * Initialize tree controls for hierarchical display.
     */
    initializeTreeControls() {
      // Cache tree elements
      this.toggleButtons = this.wrapper.querySelectorAll('.term-toggle');
      this.treeItems = this.wrapper.querySelectorAll('.hierarchical-term-item');

      // Attach tree-specific event listeners
      this.attachTreeEventListeners();

      // Add keyboard navigation support
      this.wrapper.removeEventListener('keydown', this.boundHandleKeyboardNavigation);
      this.wrapper.addEventListener('keydown', this.boundHandleKeyboardNavigation);
    }

    /**
     * Attach tree-specific event listeners.
     */
    attachTreeEventListeners() {
      // Handle expand/collapse toggles
      this.toggleButtons.forEach(button => {
        button.removeEventListener('click', this.boundHandleToggleClick);
        button.addEventListener('click', this.boundHandleToggleClick);
      });
    }

    /**
     * Initialize tree state based on selected values.
     */
    initializeTreeState() {
      // Auto-expand branches with selected items
      const selectedCheckboxes = this.wrapper.querySelectorAll('.term-checkbox:checked');
      selectedCheckboxes.forEach(checkbox => {
        this.expandParentsOfElement(checkbox);
      });
    }

    /**
     * Initialize mirrored filters.
     */
    initializeMirroredFilters() {
      this.allCheckboxes.forEach(checkbox => {
        checkbox.addEventListener('change', (event) => {
          this.handleMirroredFilterCheckboxChange(event, this.mirroredFilters, this.wrapper);
        });
      });
    }

    /**
     * On checkbox change, mirror checked state changess on this pseudo filter
     * to the mirrored real filters, accounting for possible parent-child
     * relationships.
     *
     * @param {Event} event - the checkbox change event
     * @param {Array} mirroredFilters - the list of mirrored filters (passed in member variable)
     * @param {HTMLElement} wrapper - the filter wrapper element (passed in member variable)
     */
    handleMirroredFilterCheckboxChange(event, mirroredFilters, wrapper) {
      const checkbox = event.target;
      const termId = checkbox.value;
      const isChecked = checkbox.checked;

      mirroredFilters.forEach(filter => {
        const realFilterSelector = '.js-form-item-' + filter.replace('_', '-');
        const realFilter = wrapper.closest('.pseudo-filters-container').parentElement.nextElementSibling.querySelector(realFilterSelector);

        // Mirror checked state changes.
        //
        // Case: Is hierarchical term list. Mirror the current term selection
        // and how it influences the parent or children. This is similar,
        // not identical, to handleParentChildSelection(), but covering the
        // mirrored filters in a self-contained manner.
        if (wrapper.querySelector('.hierarchical-term-list')) {
          const term = this.termMap.get(termId);

          if (!term) {
            return;
          }

          // Mirror the current term checked state.
          this.selectMirroredRealTerm(realFilter, termId, isChecked);

          // (1) If checking a parent, check all children
          // (2) If unchecking a parent, uncheck all children
          if (term.children.length > 0) {
            term.children.forEach(childId => {
              const childTerm = this.termMap.get(childId);
              if (childTerm && childTerm.checkbox) {
                this.selectMirroredRealTerm(realFilter, childTerm.id, isChecked);
              }
            });
          }

          // Set parent checked state based on collective checked state
          // of children.
          if (term.parentId) {
            const parent = this.termMap.get(term.parentId);
            if (parent) {
              const allChildrenChecked = parent.children.every(childId => {
                const child = this.termMap.get(childId);
                return child && child.checkbox && child.checkbox.checked;
              });
              if (parent.checkbox) {
                this.selectMirroredRealTerm(realFilter, parent.id, allChildrenChecked);
              }
            }
          }
        }
        // Case: Is flat list. Just mirror the current term selection.
        else {
          this.selectMirroredRealTerm(realFilter, termId, isChecked);
        }
      });
    }

    /**
     * Helper to set checked state on a specific term of a mirrored filter.
     *
     * @param {HTMLElement} realFilter - the target mirrored real filter
     * @param {string} termId - ID of the target term
     * @param {Boolean} isChecked - whether or not the target term should be checked
     */
    selectMirroredRealTerm(realFilter, termId, isChecked) {
      if (realFilter.querySelector('select')) {
        // Handle select
        const realFilterOptions = realFilter.querySelector('select').options;
        for (let i = 0; i < realFilterOptions.length; i++) {
          if (realFilterOptions[i].value === termId) {
            realFilterOptions[i].selected = isChecked;
          }
        }
      }
      else {
        // TODO: Handle checkboxes/radio buttons if needed.
      }
    }

    /**
     * Expand all parent elements of a given element.
     *
     * @param {HTMLElement} element - The element whose parents should be expanded.
     */
    expandParentsOfElement(element) {
      let parent = element.closest('.hierarchical-term-item');
      while (parent) {
        const parentItem = parent.parentElement.closest('.hierarchical-term-item');
        if (parentItem && parentItem.classList.contains('has-children')) {
          this.expandItem(parentItem);
        }
        parent = parentItem;
      }
    }

    /**
     * Build term mappings for parent-child relationships.
     * Enhanced to handle both tree and flat structures.
     */
    buildTermMappings() {
      this.termMap.clear();
      this.childToParentMap.clear();

      // Build basic checkbox mapping
      this.allCheckboxes.forEach(checkbox => {
        const termId = checkbox.value;
        const parentDepth = checkbox.dataset.parentDepth ? parseInt(checkbox.dataset.parentDepth) : null;

        this.termMap.set(termId, {
          id: termId,
          parentId: null, // Will be determined below
          checkbox,
          children: [],
          depth: parentDepth || 0
        });
      });

      if (this.enableTreeControls) {
        // For hierarchical tree structure, build relationships from DOM
        this.buildTreeMappingsFromDOM();
      }
      else {
        // For flat structure with data-children attributes
        this.buildFlatMappingsFromData();
      }

      // Also try to build mappings from the real filter if it's a SELECT
      if (this.realFilter && this.realFilter.tagName === 'SELECT') {
        this.buildMappingsFromSelect();
      }
    }

    /**
     * Build mappings from DOM tree structure.
     */
    buildTreeMappingsFromDOM() {
      this.treeItems = this.wrapper.querySelectorAll('.hierarchical-term-item');

      this.treeItems.forEach(item => {
        const checkbox = item.querySelector(':scope > .term-wrapper .term-checkbox');
        if (!checkbox) {
          return;
        }

        const termId = checkbox.value;
        const term = this.termMap.get(termId);
        if (!term) {
          return;
        }

        // Find child items
        const childList = item.querySelector(':scope > .term-children > .hierarchical-term-list');
        if (childList) {
          const childItems = childList.querySelectorAll(':scope > .hierarchical-term-item');
          childItems.forEach(childItem => {
            const childCheckbox = childItem.querySelector(':scope > .term-wrapper .term-checkbox');
            if (childCheckbox) {
              const childTermId = childCheckbox.value;
              term.children.push(childTermId);
              this.childToParentMap.set(childTermId, termId);

              // Update child's parent reference
              const childTerm = this.termMap.get(childTermId);
              if (childTerm) {
                childTerm.parentId = termId;
              }
            }
          });
        }
      });
    }

    /**
     * Build mappings from flat structure with data-children attributes.
     */
    buildFlatMappingsFromData() {
      this.allCheckboxes.forEach(checkbox => {
        const termId = checkbox.value;
        const childrenData = checkbox.dataset.children;

        if (childrenData) {
          const children = childrenData.split(',').filter(Boolean);
          const term = this.termMap.get(termId);
          if (term) {
            term.children = children;

            // Set parent references for children
            children.forEach(childId => {
              this.childToParentMap.set(childId, termId);
              const childTerm = this.termMap.get(childId);
              if (childTerm) {
                childTerm.parentId = termId;
              }
            });
          }
        }
      });
    }

    /**
     * Build mappings from SELECT element options.
     */
    buildMappingsFromSelect() {
      const options = Array.from(this.realFilter.options);
      let currentParentId = null;

      options.forEach(option => {
        const optionText = option.text.trim();
        const optionValue = option.value;

        // Check if this is a child (starts with "-")
        if (optionText.startsWith('-')) {
          // This is a child of the current parent
          if (currentParentId && this.termMap.has(currentParentId)) {
            const parent = this.termMap.get(currentParentId);
            if (!parent.children.includes(optionValue)) {
              parent.children.push(optionValue);
            }
            this.childToParentMap.set(optionValue, currentParentId);

            // Create or update child term
            if (!this.termMap.has(optionValue)) {
              this.termMap.set(optionValue, {
                id: optionValue,
                parentId: currentParentId,
                checkbox: null,
                children: []
              });
            }
            else {
              const childTerm = this.termMap.get(optionValue);
              childTerm.parentId = currentParentId;
            }
          }
        }
        else if (this.termMap.has(optionValue)) {
          // This is a parent option
          currentParentId = optionValue;
        }
        else {
          currentParentId = null;
        }
      });
    }

    /**
     * Push event to the dataLayer for tracking when a filter is selected or deselected
     *
     * @param {string} termId - ID of the target term (filter)
     * @param {boolean} isChecked - boolean indicating when filter is checked
     */
    trackFilterSelection(termId, isChecked) {
      if (this.areFiltersClearing) {
        return;
      }

      const filterGroup = this.realFilter.previousElementSibling.textContent;
      const termParentId = this.childToParentMap.get(termId);
      const realFilterOption = Array.from(this.realFilter.options).find(
        option => option.value === termId
      );
      let realFilterParentOption = null;
      let realFilterParentOptionText = '';
      let realFilterOptionText = realFilterOption.textContent;

      if (termParentId) {
        realFilterParentOption = Array.from(this.realFilter.options).find(
          option => option.value === termParentId
        );
      }

      if (realFilterParentOption) {
        realFilterParentOptionText = `${realFilterParentOption.textContent} >> `;
        realFilterOptionText = realFilterOptionText.slice(1);
      }

      dataLayer.push({
        'event': isChecked ? 'add-filter' : 'remove-filter',
        'data-analytics-filter-group': filterGroup,
        'data-analytics-filter-selection': realFilterParentOptionText + realFilterOptionText
      });
    }

    /**
     * Sync the real SELECT filter with the current state of all checkboxes.
     * This is a simplified approach that just looks at what's checked and updates the real filter.
     */
    syncRealFilterWithCheckboxes() {
      if (!this.realFilter || this.realFilter.tagName !== 'SELECT') {
        return;
      }

      // Check if real filter is still connected to DOM
      if (!this.realFilter.isConnected) {
        this.findRealFilter();
        if (!this.realFilter) {
          return;
        }
      }

      // Set a flag to prevent syncing back from the real filter
      this.updatingRealFilter = true;

      // Collect all selected term IDs from checked checkboxes
      const selectedTermIds = new Set();

      // Go through all checkboxes and collect checked ones
      this.allCheckboxes.forEach(checkbox => {
        if (checkbox.checked && !checkbox.indeterminate) {
          selectedTermIds.add(checkbox.value);
          // For non-tree controls, when a parent is checked, include its children
          if (!this.enableTreeControls) {
            const term = this.termMap.get(checkbox.value);
            if (term && term.children && term.children.length > 0) {
              // Add all children recursively
              this.addChildrenToSet(term.children, selectedTermIds);
            }
          }
        }
        // Note: indeterminate checkboxes are not selected
      });


      // Update the SELECT element - clear all and set only selected ones
      let selectUpdates = [];
      let actualChanges = 0;

      Array.from(this.realFilter.options).forEach(option => {
        const wasSelected = option.selected;
        const shouldBeSelected = selectedTermIds.has(option.value);

        // Force the selection state
        option.selected = shouldBeSelected;

        if (wasSelected !== shouldBeSelected) {
          selectUpdates.push({
            value: option.value,
            text: option.text.trim(),
            wasSelected: wasSelected,
            nowSelected: shouldBeSelected
          });
          actualChanges++;
        }
      });



      // Force revalidation of SELECT state by temporarily changing and restoring
      if (actualChanges > 0) {

        // Store current state
        const selectedValues = Array.from(this.realFilter.selectedOptions || []).map(opt => opt.value);

        // Temporarily clear all selections
        Array.from(this.realFilter.options).forEach(option => {
          option.selected = false;
        });

        // Restore correct selections
        Array.from(this.realFilter.options).forEach(option => {
          option.selected = selectedValues.includes(option.value);
        });
      }

      // Trigger change event on the real filter
      const event = new Event('change', {bubbles: true, cancelable: true});
      this.realFilter.dispatchEvent(event);

      // Clear the flag immediately instead of using setTimeout to avoid race conditions
      this.updatingRealFilter = false;
    }

    /**
     * Handle hierarchical selection with indeterminate states.
     *
     * @param {string} termId - The term ID.
     * @param {boolean} isChecked - Whether the checkbox is checked.
     */
    handleHierarchicalSelection(termId, isChecked) {
      const term = this.termMap.get(termId);
      if (!term) {
        return;
      }

      const listItem = term.checkbox ? term.checkbox.closest('.hierarchical-term-item') : null;

      // If this is a parent checkbox, update all children
      if (listItem && listItem.classList.contains('has-children')) {
        if (isChecked) {
          // When checking parent, check all children
          const childCheckboxes = listItem.querySelectorAll('.term-children .term-checkbox');
          childCheckboxes.forEach(childCheckbox => {
            childCheckbox.checked = true;
            childCheckbox.indeterminate = false;
          });
        }
        else {
          // When unchecking parent, uncheck all children recursively
          this.uncheckAllChildren(listItem);
        }
      }

      // Update parent checkboxes based on children (indeterminate state)
      this.updateParentCheckboxStates(term.checkbox);
    }

    /**
     * Recursively uncheck all children of a given list item.
     *
     * @param {HTMLElement} listItem - The parent list item.
     */
    uncheckAllChildren(listItem) {
      const childCheckboxes = listItem.querySelectorAll('.term-children .term-checkbox');
      childCheckboxes.forEach(childCheckbox => {
        childCheckbox.checked = false;
        childCheckbox.indeterminate = false;

        // Clear any indeterminate state on child items that might be parents themselves
        const childListItem = childCheckbox.closest('.hierarchical-term-item');
        if (childListItem && childListItem.classList.contains('has-children')) {
          // Recursively process any nested children
          const nestedCheckboxes = childListItem.querySelectorAll('.term-children .term-checkbox');
          nestedCheckboxes.forEach(nestedCheckbox => {
            nestedCheckbox.checked = false;
            nestedCheckbox.indeterminate = false;
          });
        }
      });
    }

    /**
     * Update parent checkbox states based on children.
     *
     * @param {HTMLElement} checkbox - The changed checkbox.
     */
    updateParentCheckboxStates(checkbox) {
      if (!checkbox) {
        return;
      }

      const listItem = checkbox.closest('.hierarchical-term-item');
      if (!listItem) {
        return;
      }

      // Find parent list item
      const parentList = listItem.parentElement.closest('.term-children');
      if (!parentList) {
        return;
      }

      const parentItem = parentList.parentElement;
      if (!parentItem || !parentItem.classList.contains('hierarchical-term-item')) {
        return;
      }

      const parentCheckbox = parentItem.querySelector(':scope > .term-wrapper .term-checkbox');
      if (!parentCheckbox) {
        return;
      }

      // Check states of all sibling checkboxes
      const siblingCheckboxes = parentList.querySelectorAll('.term-checkbox');
      const checkedCount = Array.from(siblingCheckboxes).filter(cb => cb.checked).length;

      if (checkedCount === 0) {
        // No children checked
        parentCheckbox.checked = false;
        parentCheckbox.indeterminate = false;
      }
      else if (checkedCount === siblingCheckboxes.length) {
        // All children checked
        parentCheckbox.checked = true;
        parentCheckbox.indeterminate = false;
      }
      else {
        // Some children checked
        parentCheckbox.indeterminate = true;
      }

      // Recursively update parent's parent
      this.updateParentCheckboxStates(parentCheckbox);
    }

    /**
     * Handle toggle button click.
     *
     * @param {Event} event - The click event.
     */
    handleToggleClick(event) {
      event.preventDefault();
      const button = event.currentTarget;
      const listItem = button.closest('.hierarchical-term-item');

      // Check if listItem exists before accessing its classList
      if (!listItem) {
        return;
      }

      if (listItem.classList.contains('is-expanded')) {
        this.collapseItem(listItem);
      }
      else {
        this.expandItem(listItem);
      }
    }

    /**
     * Expand a tree item.
     *
     * @param {HTMLElement} item - The list item to expand.
     */
    expandItem(item) {
      if (!item.classList.contains('has-children')) {
        return;
      }

      item.classList.add('is-expanded');
      const toggle = item.querySelector('.term-toggle');
      const children = item.querySelector('.term-children');

      if (toggle) {
        toggle.setAttribute('aria-expanded', 'true');
        toggle.classList.add('is-expanded');
      }

      if (children) {
        children.classList.remove('visually-hidden');
        children.setAttribute('aria-hidden', 'false');
      }
    }

    /**
     * Collapse a tree item.
     *
     * @param {HTMLElement} item - The list item to collapse.
     */
    collapseItem(item) {
      if (!item.classList.contains('has-children')) {
        return;
      }

      item.classList.remove('is-expanded');
      const toggle = item.querySelector('.term-toggle');
      const children = item.querySelector('.term-children');

      if (toggle) {
        toggle.setAttribute('aria-expanded', 'false');
        toggle.classList.remove('is-expanded');
      }

      if (children) {
        children.classList.add('visually-hidden');
        children.setAttribute('aria-hidden', 'true');
      }
    }

    /**
     * Handle keyboard navigation.
     *
     * @param {KeyboardEvent} event - The keyboard event.
     */
    handleKeyboardNavigation(event) {
      if (!this.enableTreeControls) {
        return;
      }

      const activeElement = document.activeElement;

      // Only handle navigation within the tree
      if (!this.wrapper.contains(activeElement)) {
        return;
      }

      const key = event.key;
      const listItem = activeElement.closest('.hierarchical-term-item');

      if (!listItem) {
        return;
      }

      switch (key) {
        case 'ArrowRight':
          // Expand if has children and collapsed
          if (listItem.classList.contains('has-children') && !listItem.classList.contains('is-expanded')) {
            this.expandItem(listItem);
            event.preventDefault();
          }
          break;

        case 'ArrowLeft':
          // Collapse if has children and expanded
          if (listItem.classList.contains('has-children') && listItem.classList.contains('is-expanded')) {
            this.collapseItem(listItem);
            event.preventDefault();
          }
          break;

        case 'ArrowDown':
          // Move to next visible item
          this.focusNextItem(listItem);
          event.preventDefault();
          break;

        case 'ArrowUp':
          // Move to previous visible item
          this.focusPreviousItem(listItem);
          event.preventDefault();
          break;

        case ' ':
        case 'Enter':
          // Toggle expand/collapse if focused on toggle button.
          if (activeElement.closest('.term-toggle')) {
            if (listItem.classList.contains('has-children')) {
              if (listItem.classList.contains('is-expanded')) {
                this.collapseItem(listItem);
              }
              else {
                this.expandItem(listItem);
              }
              event.preventDefault();
            }
          }

          // Toggle checkbox if focused on label.
          if (activeElement.classList.contains('term-label')) {
            const checkbox = listItem.querySelector(':scope > .term-wrapper .term-checkbox');
            if (checkbox) {
              checkbox.click();
              event.preventDefault();
            }
          }
          break;
      }
    }

    /**
     * Focus the next visible item in the tree.
     *
     * @param {HTMLElement} currentItem - The current list item.
     */
    focusNextItem(currentItem) {
      // If expanded and has children, go to first child
      if (currentItem.classList.contains('is-expanded')) {
        const firstChild = currentItem.querySelector('.term-children .hierarchical-term-item');
        if (firstChild) {
          const target = firstChild.querySelector('.term-checkbox, .term-toggle');
          if (target) {
            target.focus();
            return;
          }
        }
      }

      // Try next sibling
      const nextSibling = currentItem.nextElementSibling;
      if (nextSibling && nextSibling.classList.contains('hierarchical-term-item')) {
        const target = nextSibling.querySelector('.term-checkbox, .term-toggle');
        if (target) {
          target.focus();
          return;
        }
      }

      // Go up to parent and try parent's next sibling
      const parentList = currentItem.parentElement.closest('.term-children');
      if (parentList) {
        const parentItem = parentList.parentElement;
        if (parentItem && parentItem.classList.contains('hierarchical-term-item')) {
          this.focusNextItem(parentItem);
        }
      }
    }

    /**
     * Focus the previous visible item in the tree.
     *
     * @param {HTMLElement} currentItem - The current list item.
     */
    focusPreviousItem(currentItem) {
      // Try previous sibling
      const prevSibling = currentItem.previousElementSibling;
      if (prevSibling && prevSibling.classList.contains('hierarchical-term-item')) {
        // If previous sibling is expanded, go to its last visible child
        if (prevSibling.classList.contains('is-expanded')) {
          const lastChild = this.getLastVisibleChild(prevSibling);
          const target = lastChild.querySelector('.term-checkbox, .term-toggle');
          if (target) {
            target.focus();
            return;
          }
        }
        else {
          const target = prevSibling.querySelector('.term-checkbox, .term-toggle');
          if (target) {
            target.focus();
            return;
          }
        }
      }

      // Go to parent
      const parentList = currentItem.parentElement.closest('.term-children');
      if (parentList) {
        const parentItem = parentList.parentElement;
        if (parentItem && parentItem.classList.contains('hierarchical-term-item')) {
          const target = parentItem.querySelector('.term-checkbox, .term-toggle');
          if (target) {
            target.focus();
          }
        }
      }
    }

    /**
     * Get the last visible child of an expanded item.
     *
     * @param {HTMLElement} item - The parent item.
     * @return {HTMLElement} The last visible child item.
     */
    getLastVisibleChild(item) {
      if (!item.classList.contains('is-expanded')) {
        return item;
      }

      const children = item.querySelectorAll('.term-children > .hierarchical-term-list > .hierarchical-term-item');
      if (children.length === 0) {
        return item;
      }

      const lastChild = children[children.length - 1];
      return this.getLastVisibleChild(lastChild);
    }

    /**
     * Recursively add children to the selected set.
     *
     * @param {Array} children - Array of child term IDs.
     * @param {Set} selectedSet - Set to add terms to.
     */
    addChildrenToSet(children, selectedSet) {
      children.forEach(childId => {
        selectedSet.add(childId);
        const childTerm = this.termMap.get(childId);
        if (childTerm && childTerm.children && childTerm.children.length > 0) {
          this.addChildrenToSet(childTerm.children, selectedSet);
        }
      });
    }

    /**
     * Sync pseudo filter with real filter values.
     * Override to handle parent-child relationships properly after AJAX.
     */
    syncWithRealFilter() {
      // Don't sync if we're currently updating the real filter
      if (this.updatingRealFilter) {
        return;
      }

      if (!this.realFilter) {
        return;
      }

      let checkedValues = [];

      // Check if real filter is a SELECT element
      if (this.realFilter.tagName === 'SELECT') {
        if (this.realFilter.multiple) {
          checkedValues = Array.from(this.realFilter.selectedOptions).map(opt => opt.value);
        }
        else {
          checkedValues = this.realFilter.value ? [this.realFilter.value] : [];
        }
      }
      else {
        // Original checkbox handling
        const realCheckboxes = this.realFilter.querySelectorAll('input[type="checkbox"]:checked');
        checkedValues = Array.from(realCheckboxes).map(cb => cb.value);
      }

      // First, uncheck all checkboxes
      this.allCheckboxes.forEach(checkbox => {
        checkbox.checked = false;
        checkbox.indeterminate = false;
      });

      // Then check only the values from the real filter
      checkedValues.forEach(value => {
        const checkbox = Array.from(this.allCheckboxes).find(cb => cb.value === value);
        if (checkbox) {
          checkbox.checked = true;
        }
      });

      // Update parent checkbox states based on children for hierarchical display
      if (this.enableTreeControls) {
        // Update indeterminate states for parents based on their children
        this.allCheckboxes.forEach(checkbox => {
          if (checkbox.checked) {
            this.updateParentCheckboxStates(checkbox);
          }
        });
      }
    }

    /**
     * Reconnect handler after AJAX update.
     * Enhanced to reinitialize tree controls.
     */
    reconnectAfterAjax() {
      super.reconnectAfterAjax();

      if (this.enableTreeControls) {
        this.initializeTreeControls();
        this.initializeTreeState();
      }
    }
  }

  // Export to window for global access
  window.TaxonomyPseudoFilterHandler = TaxonomyPseudoFilterHandler;

})(jQuery, Drupal, once, window.dataLayer);
